import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useApp, API } from "../store";
import { Heart, X } from "lucide-react";

export function Wishlist() {
  const { wishlist, toggleWishlist } = useApp();
  const [products, setProducts] = useState([]);
  useEffect(() => {
    Promise.all(wishlist.map((s) => axios.get(`${API}/products/${s}`).then((r) => r.data).catch(() => null)))
      .then((res) => setProducts(res.filter(Boolean)));
  }, [wishlist]);

  return (
    <main className="pt-32 pb-32 bg-black min-h-screen" data-testid="wishlist-page">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="overline mb-4">{t.wishlist.title}</div>
        <h1 className="font-serif-display text-5xl md:text-7xl">{t.wishlist.title}</h1>
        {products.length === 0 ? (
          <div className="mt-20 text-center text-white/60">
            <Heart size={48} className="mx-auto mb-4 opacity-40" />
            <p>{t.wishlist.empty}</p>
          </div>
        ) : (
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {products.map((p) => (
              <div key={p.id} className="relative group border border-white/10 bg-[#111]">
                <button onClick={() => toggleWishlist(p.slug)} className="absolute top-3 right-3 z-10 p-2 bg-black/60 hover:bg-black text-white" data-testid={`wish-remove-${p.slug}`}><X size={16} /></button>
                <Link to={`/product/${p.slug}`}>
                  <img src={p.images?.[0]} alt={p.name} className="w-full aspect-[4/5] object-cover" />
                  <div className="p-5">
                    <div className="font-serif-display text-2xl">{p.name}</div>
                    <div className="text-white/60 text-xs mt-1">{p.tagline}</div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

export function Compare() {
  const { compare, toggleCompare } = useApp();
  const [allProducts, setAllProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");

  // Buscar todos os produtos da API (exceto acessórios)
  useEffect(() => {
    axios.get(`${API}/products`)
      .then((r) => {
        const filtered = (r.data || []).filter((p) => !p.is_accessory);
        setAllProducts(filtered);
      })
      .catch(() => setAllProducts([]));
  }, []);

  // Filtrar apenas os selecionados para comparação
  useEffect(() => {
    const selected = allProducts.filter((p) => compare.includes(p.slug));
    setProducts(selected);
  }, [compare, allProducts]);

  const allSpecs = Array.from(new Set(products.flatMap((p) => Object.keys(p.specs || {}))));

  // Produtos disponíveis para adicionar (filtrados por nível)
  const availableProducts = allProducts.filter((p) => {
    if (compare.includes(p.slug)) return false;
    if (activeFilter === "all") return true;
    return p.levels && p.levels.includes(activeFilter);
  });

  // Contar produtos por nível
  const countAll = allProducts.filter((p) => !compare.includes(p.slug)).length;
  const countBeginner = allProducts.filter((p) => !compare.includes(p.slug) && p.levels && p.levels.includes("beginner")).length;
  const countIntermediate = allProducts.filter((p) => !compare.includes(p.slug) && p.levels && p.levels.includes("intermediate")).length;
  const countPro = allProducts.filter((p) => !compare.includes(p.slug) && p.levels && p.levels.includes("pro")).length;

  const filters = [
    { key: "all", label: "Todos", count: countAll },
    { key: "beginner", label: "Iniciante", count: countBeginner },
    { key: "intermediate", label: "Intermediário", count: countIntermediate },
    { key: "pro", label: "Pro", count: countPro },
  ];

  return (
    <main className="pt-32 pb-32 bg-[#f6f6f3] min-h-screen text-[#0b111e]" data-testid="compare-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="overline mb-4 text-[#0b111e]/60">{t.comparePage.overline}</div>
        <h1 className="font-serif-display text-5xl md:text-7xl">{t.comparePage.title}</h1>

        {/* Slider de filtro por nível */}
        <div className="mt-10">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-ui whitespace-nowrap transition-all ${
                  activeFilter === f.key
                    ? "bg-[#0b111e] text-white shadow-lg"
                    : "bg-white border border-slate-200 text-[#0b111e]/70 hover:border-slate-400"
                }`}
              >
                {f.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeFilter === f.key ? "bg-white/20" : "bg-slate-100"
                }`}>{f.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Seleção de produtos */}
        {availableProducts.length > 0 && (
          <div className="mt-8">
            <h2 className="font-ui text-xs uppercase tracking-[0.2em] text-[#0b111e]/60 mb-4">
              {t.comparePage.addLabel}
            </h2>
            <div className="flex flex-wrap gap-4">
              {availableProducts.map((p) => (
                <button
                  key={p.id}
                  onClick={() => toggleCompare(p.slug)}
                  className="flex items-center gap-4 px-5 py-3 bg-white border border-slate-200 rounded-xl hover:border-sky-500 hover:shadow-lg transition-all text-left min-w-[220px]"
                  data-testid={`cmp-add-${p.slug}`}
                >
                  <img src={p.images?.[0]} alt={p.name} className="w-14 h-14 object-cover rounded-lg flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-ui text-sm font-medium text-[#0b111e] truncate">{p.name}</div>
                    <div className="text-[#0b111e]/50 text-xs mt-0.5 truncate">{p.tagline}</div>
                    <div className="text-sky-500 text-sm mt-1">${p.base_price.toLocaleString()}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {availableProducts.length === 0 && activeFilter !== "all" && (
          <div className="mt-8 text-center text-[#0b111e]/40 text-sm">
            {t.comparePage.noProducts}
          </div>
        )}

        {/* Tabela de comparação */}
        {products.length === 0 ? (
          <div className="mt-20 text-center text-[#0b111e]/60">
            {t.comparePage.selectProducts}
          </div>
        ) : (
          <div className="mt-14 grid gap-4" style={{ gridTemplateColumns: `repeat(${products.length}, minmax(0, 1fr))` }}>
            {products.map((p) => (
              <div key={p.id} className="border border-slate-200 bg-white">
                <div className="relative">
                  <img src={p.images?.[0]} alt={p.name} className="w-full aspect-[4/5] object-cover" />
                  <button onClick={() => toggleCompare(p.slug)} className="absolute top-3 right-3 p-2 bg-[#f6f6f3]/80 hover:bg-[#f6f6f3] text-[#0b111e]" data-testid={`cmp-remove-${p.slug}`}><X size={16} /></button>
                </div>
                <div className="p-5">
                  <div className="font-serif-display text-2xl">{p.name}</div>
                  <div className="text-[#0b111e]/50 text-xs mt-1">{p.tagline}</div>
                  <div className="text-sky-500 font-ui text-sm mt-2">${p.base_price.toLocaleString()}</div>
                  <div className="mt-4 space-y-2 border-t border-slate-200 pt-4 text-sm">
                    {allSpecs.map((k) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-[#0b111e]/50 uppercase text-[11px] tracking-widest">{k.replace(/_/g, " ")}</span>
                        <span className="text-[#0b111e]">{p.specs?.[k] ?? "—"}</span>
                      </div>
                    ))}
                  </div>
                  <Link to={`/product/${p.slug}`} className="block mt-5 text-xs uppercase tracking-[0.2em] font-ui text-sky-600 hover:text-sky-700">{t.comparePage.configure} →</Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
